import { RecommendationInfoButton } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationInfoCard';
import type { InfoCardScope } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationInfoCard.utilities';
import { DateTableOrderedCells, AvailabilityCell, useDateTableColumns } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DateTableColumns';
import { PmsScope, pmsPickup } from '@app/model/pricing-automation/service/PmsMetrics';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import { usePricingRights } from '@util/PricingRightsHook';
import React, { useEffect, useMemo, useState } from 'react';
import Utilities from '@util/Utilities';
import {
	MinusCircleIcon,
	PlusCircleIcon,
	PresentationChartLineIcon,
} from '@heroicons/react/24/solid';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import CalendarDay from '@app/state/calendar/CalendarDay';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import _isEmpty from 'lodash/isEmpty';
import HotelRoomMappingType, {
	sortRoomMappingTypes,
} from '@app/model/market_state/HotelRoomMappingType';
import MarketStateDailySnapshotState from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import {
	CompetitorSnapshotsComparison,
	DisplayMarketStateSnapshotTableHotelPricePureFragment,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DisplayMarketStateSnapshotTableHotelPriceFragment';
import MarketStateSnapshotTableMedianFragment from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotTableMedianFragment';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { useLatestProcessRequestExecutionStateMonitor } from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';
import Process from '@app/model/process-request/Process';
import { PickupCell, pickupFor, PickupWindow } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/PickupCell';
import {
	RecommendationPercentLine,
	RecommendationRightControl,
	RecommendationSubmitState,
	useRecommendationSubmit,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationCellActions';
import {
	findValidRecommendationStates,
	findDateRowRecommendation,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotDateBasedTableRowFragment.utilities';

let dateRecommendationDragSelected: boolean | undefined = undefined;

export const clearRecommendationDateDrag = () => {
	dateRecommendationDragSelected = undefined;
};

type MarketStateSnapshotDateBasedTableRowFragmentProps = {
	dailySnapshotState: MarketStateDailySnapshotState;
	onSelectedCalendarDay: (calendarDay: CalendarDay) => void;
	detailsRequestId: number;
	enforceOpenDetails: boolean;
	dateSelectable: boolean;
	dateSelected: boolean;
	bulkFailureReason?: string;
	onRecommendationSelectedChange: (statDate: string, selected: boolean) => void;
	// The Pickup of the date and the windows to show; undefined = no data for the date.
	pace?: PmsScope[];
	pickupWindows: PickupWindow[];
	showPickup: boolean;
	// The user can push prices from the row.
	canSubmitPrices: boolean;
};

const MarketStateSnapshotDateBasedTableRowFragment: React.FunctionComponent<
	MarketStateSnapshotDateBasedTableRowFragmentProps
> = ({
	dailySnapshotState,
	onSelectedCalendarDay,
	detailsRequestId,
	enforceOpenDetails,
	dateSelectable,
	dateSelected,
	bulkFailureReason,
	onRecommendationSelectedChange,
	pace,
	pickupWindows,
	showPickup,
	canSubmitPrices,
}) => {
	const { companyHotel } = useSelectedCompanyHotel();
	// The reason of a blocked push from this row, under the date (the ROO2-72 style).
	const [submitFailure, setSubmitFailure] = useState<string | undefined>(undefined);
	const marketStateTools = useMarketStateTools();
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const [localDetailsState, setLocalDetailsState] = useState({
		opened: false,
		requestId: -1,
	});
	const [competitorSnapshotsComparison, setCompetitorSnapshotsComparison] =
		useState<CompetitorSnapshotsComparison>([]);

	const { filter } = useMarketStateFilterInputTools();
	const visibleCompetitors = companyHotel.visibleCompetitors(filter.competitorTagIds);
	const columns = useDateTableColumns();
	const hasAnyRoomType = filter.roomTypes.length === 0;
	const isAnyProvider = filter.provider === undefined;
	const showUpdateSuccess =
		dailySnapshotState.calendarDay.isCurrentMonth &&
		!dailySnapshotState.calendarDay.isPast &&
		isMarketStateTableProcess(monitor.latestCompletedExecutionState);
	const isUpdating =
		dailySnapshotState.calendarDay.isCurrentMonth &&
		!dailySnapshotState.calendarDay.isPast &&
		monitor.isInteractionLocked &&
		isMarketStateTableProcess(monitor.latestActiveExecutionState);

	const opened =
		localDetailsState.requestId === detailsRequestId
			? localDetailsState.opened
			: enforceOpenDetails;

	useEffect(() => {
		let cancelled = false;
		setCompetitorSnapshotsComparison([]);

		if (
			!dailySnapshotState.needsRefreshResults() &&
			!!dailySnapshotState.marketStateDailySnapshot
		) {
			dailySnapshotState.marketStateDailySnapshot
				.getCompetitorSnapshotsComparison(marketStateTools.findPreviousSnapshot)
				.then(snapshots => {
					if (!cancelled) setCompetitorSnapshotsComparison(snapshots);
				})
				.catch(() => {
					if (!cancelled) setCompetitorSnapshotsComparison([]);
				});
		}

		return () => {
			cancelled = true;
		};
	}, [dailySnapshotState.marketStateDailySnapshot, marketStateTools.findPreviousSnapshot]);

	const { canViewRecommendations } = usePricingRights(companyHotel);
	const recommendationStates = findValidRecommendationStates(dailySnapshotState, companyHotel, canViewRecommendations);
	const dateRowRecommendation = findDateRowRecommendation(
		dailySnapshotState,
		recommendationStates
	);
	const mainRecommendationState = dateRowRecommendation?.recommendation;
	const hidesOtherRoomType = dateRowRecommendation?.showRecommendedPrice === false
		&& !companyHotel.settings.calendar.showOtherRoomTypeRecommendation;
	const showRecommendation =
		mainRecommendationState !== undefined && mainRecommendationState.isValid && !hidesOtherRoomType;
	const submittedRecommendationStateIds = new Set(
		dailySnapshotState.marketStateDailySnapshot?.submittedRecommendationStateIds ?? []
	);
	// The date row acts on every open room type of the date.
	const openRecommendationIds = recommendationStates
		.filter(state => !submittedRecommendationStateIds.has(state.id))
		.map(state => state.id);
	const failureReason = submitFailure ?? bulkFailureReason;
	const dateRecommendedPrice = dateRowRecommendation?.showRecommendedPrice === true
		? findRecommendedPrice(dailySnapshotState, mainRecommendationState, mainRecommendationState?.roomMappingType)
		: undefined;
	const submit = useRecommendationSubmit({
		companyHotel,
		statDate: dailySnapshotState.calendarDay.display,
		marketUpdatedAt: dailySnapshotState.marketStateDailySnapshot?.current.latestUpdateDate,
		recommendationIds: openRecommendationIds,
		recommendedPrice: dateRecommendedPrice,
		effectiveChangeRate: mainRecommendationState?.effectiveChangeRate,
		recommendationStates: recommendationStates.filter(state => openRecommendationIds.includes(state.id))
			.sort((a, b) => Number(b.id === mainRecommendationState?.id) - Number(a.id === mainRecommendationState?.id)),
		hotelMarketPrice: dateRowRecommendation?.showRecommendedPrice === true
			? findHotelMarketPrice(dailySnapshotState, mainRecommendationState?.roomMappingType)
			: undefined,
		enabled: canSubmitPrices && !dailySnapshotState.calendarDay.isPast && openRecommendationIds.length > 0 && showRecommendation,
		onBlocked: setSubmitFailure,
	});
	const toggleRecommendationsForDate = () => {
		if (!dateSelectable) return;

		const selected = !dateSelected;
		dateRecommendationDragSelected = selected;
		onRecommendationSelectedChange(dailySnapshotState.calendarDay.display, selected);
	};
	const toggleRecommendationsForDateOnDrag = (event: React.MouseEvent<HTMLTableCellElement>) => {
		if (!dateSelectable || event.buttons !== 1 || dateRecommendationDragSelected === undefined)
			return;
		if (dateSelected === dateRecommendationDragSelected) return;

		onRecommendationSelectedChange(
			dailySnapshotState.calendarDay.display,
			dateRecommendationDragSelected
		);
	};

	return (
		<React.Fragment>
			<tr
				className={Utilities.classNames(
					'rates-date-row h-12',
					dailySnapshotState.calendarDay.isToday ? 'bg-blue-50 font-bold' : '',
					dailySnapshotState.calendarDay.isPast ? 'opacity-30 bg-gray-200/60' : ''
				)}
			>
				<td className="whitespace-nowrap px-2 py-1 align-middle text-sm font-medium text-gray-800 border-r border-gray-200">
					{dateSelectable && (
						<FormCheckbox name={`recommendationDate-${dailySnapshotState.calendarDay.display}`}
							label={`Select ${Utilities.formatDate(dailySnapshotState.calendarDay.date, 'ddd DD MMM')}`}
							labelExtraClassName="sr-only" checked={dateSelected}
							onChange={event => onRecommendationSelectedChange(dailySnapshotState.calendarDay.display, event.target.checked)} />
					)}
				</td>
				<td
					className={Utilities.classNames(
						'whitespace-nowrap px-2 py-1 align-middle text-sm font-medium text-gray-800 border-r border-gray-200',
						dateSelectable ? 'cursor-pointer select-none hover:bg-blue-50' : '',
						failureReason !== undefined && failureReason !== '' ? 'bg-red-50 text-red-700' : ''
					)}
					onMouseDown={event => {
						if (event.button !== 0) return;
						event.preventDefault();
						toggleRecommendationsForDate();
					}}
					onMouseEnter={toggleRecommendationsForDateOnDrag}
					onMouseUp={clearRecommendationDateDrag}
				>
					{Utilities.formatDate(dailySnapshotState.calendarDay.date, 'ddd DD/MM')}

				</td>
				<td className="whitespace-nowrap px-2 py-1 align-middle text-sm font-medium text-gray-800 border-r border-gray-200">
					{!dailySnapshotState.needsRefreshResults() && (
						<a
							className={Utilities.classNames(
								'flex justify-center items-center text-gray-500 hover:text-blue-500 cursor-pointer',
								!hasAnyRoomType ? 'text-gray-300 pointer-events-none' : ''
							)}
							onClick={() =>
								hasAnyRoomType
									? setLocalDetailsState({
											opened: !opened,
											requestId: detailsRequestId,
										})
									: undefined
							}
						>
							{opened ? (
								<MinusCircleIcon className="w-4 h-4" />
							) : (
								<PlusCircleIcon className="w-4 h-4" />
							)}
						</a>
					)}
				</td>
				<td className="whitespace-nowrap px-2 py-1 align-middle text-sm text-gray-500 border-r border-gray-200">
					<a
						className="flex justify-center items-center text-gray-500 hover:text-blue-500 cursor-pointer"
						data-tooltip-id={`table_tooltip_${dailySnapshotState.calendarDay.display}`}
						onClick={() => onSelectedCalendarDay(dailySnapshotState.calendarDay)}
					>
						<PresentationChartLineIcon className="w-3.5 h-3.5" />
					</a>
				</td>
				<td className="whitespace-nowrap px-1.5 py-1 align-middle text-sm text-gray-500 border-r border-gray-200">
					<DisplayMarketStateSnapshotTableHotelPricePureFragment
						marketStateDailySnapshotState={dailySnapshotState}
						competitorSnapshots={competitorSnapshotsComparison}
						companyHotel={companyHotel}
						isAnyProvider={isAnyProvider}
						showUpdateSuccess={showUpdateSuccess}
						isUpdating={isUpdating}
					/>
				</td>
				<DateTableOrderedCells
 recommendation={<RenderRecommendationPriceCell
						recommendedPrice={dateRecommendedPrice}
						effectiveChangeRate={mainRecommendationState?.effectiveChangeRate}
						companyHotel={companyHotel}
						showRecommendation={showRecommendation}
						isSubmitted={recommendationStates.length > 0 && openRecommendationIds.length === 0}
						showRecommendedPrice={dateRowRecommendation?.showRecommendedPrice}
						showAsterisk={dateRowRecommendation?.showRecommendedPrice === false}
						editor={submit}
						info={{ statDate: dailySnapshotState.calendarDay.display, scope: 'date' }}
					/>}
 pickup={<PickupCell pickup={pmsPickup(pace)} windows={pickupWindows}
																		label={Utilities.formatDate(dailySnapshotState.calendarDay.date, 'ddd DD/MM')} />}
 availability={<AvailabilityCell statDate={dailySnapshotState.calendarDay.display} />}
 median={<td className="whitespace-nowrap px-2 py-1 align-middle text-sm text-gray-400 bg-blue-50 border-r border-gray-200 text-center">
					<MarketStateSnapshotTableMedianFragment calendarDay={dailySnapshotState.calendarDay} />
				</td>}
 competitors={visibleCompetitors.map(competitor => (
					<td
						className="whitespace-nowrap px-1.5 py-1 align-middle text-sm text-gray-500 border-r"
						key={competitor.id}
					>
						<DisplayMarketStateSnapshotTableHotelPricePureFragment
							marketStateDailySnapshotState={dailySnapshotState}
							competitor={competitor}
							competitorSnapshots={competitorSnapshotsComparison}
							companyHotel={companyHotel}
							isAnyProvider={isAnyProvider}
							showUpdateSuccess={showUpdateSuccess}
							isUpdating={isUpdating}
						/>
					</td>
				))}
 />
			</tr>
			{failureReason !== undefined && failureReason !== '' && <tr><td colSpan={5 + columns.visible.length}
				className="bg-red-50 px-3 py-2 text-xs text-red-700"><span role="alert" className="sticky left-0 block w-fit max-w-xs whitespace-normal break-words">{failureReason}</span></td></tr>}
			{!dailySnapshotState.needsRefreshResults() && opened && hasAnyRoomType && (
				<MarketStateSnapshotDateBasedTableRowDetailsFragment
					dailySnapshotState={dailySnapshotState}
					companyHotel={companyHotel}
					competitorSnapshotsComparison={competitorSnapshotsComparison}
					isAnyProvider={isAnyProvider}
					showUpdateSuccess={showUpdateSuccess}
					isUpdating={isUpdating}
					pace={pace}
					pickupWindows={pickupWindows}
					showPickup={showPickup}
					canSubmitPrices={canSubmitPrices && !dailySnapshotState.calendarDay.isPast}
				/>
			)}
		</React.Fragment>
	);
};

// The Hotel Market Price of the row: the price of the price column (the lowest item of the main hotel,
// for the date or for one room type). It is the base of the Rec Price and of the Market estimate.
export const findHotelMarketPrice = (
	dailySnapshotState: MarketStateDailySnapshotState,
	roomMappingType?: HotelRoomMappingType
): number | undefined => {
	const mainHotelSnapshot =
		dailySnapshotState.marketStateDailySnapshot?.current.findDailyMainHotelSnapshot();
	const mainHotelLowestItem = !roomMappingType
		? mainHotelSnapshot?.getLowestItemForRoom()
		: mainHotelSnapshot?.getLowestItemForRoomMappingType(roomMappingType);
	const mainHotelPrice = mainHotelLowestItem?.lowestItem?.item?.price?.amount;

	if (mainHotelPrice === undefined || mainHotelPrice === null) return undefined;
	return Number(mainHotelPrice);
};

export const findRecommendedPrice = (
	dailySnapshotState: MarketStateDailySnapshotState,
	recommendationState: PriceRecommendationState | undefined,
	roomMappingType?: HotelRoomMappingType
) => {
	if (!recommendationState) return undefined;
	const mainHotelPrice = findHotelMarketPrice(dailySnapshotState, roomMappingType);
	if (mainHotelPrice === undefined) return undefined;
	return mainHotelPrice * (1 + Number(recommendationState.effectiveChangeRate));
};

const RenderRecommendationPriceCell: React.FunctionComponent<{
	recommendedPrice: number | undefined;
	effectiveChangeRate: number | undefined;
	companyHotel: CompanyHotel;
	showRecommendation: boolean;
	isSubmitted: boolean;
	showRecommendedPrice?: boolean;
	showAsterisk?: boolean;
	// The row owns the request state; the editor opens in a panel.
	editor?: RecommendationSubmitState;
	info: { statDate: string, scope: InfoCardScope };
}> = ({
	recommendedPrice,
	effectiveChangeRate,
	companyHotel,
	showRecommendation,
	isSubmitted,
	showRecommendedPrice = true,
	showAsterisk = false,
	editor,
	info,
}) => {
	if (
		!showRecommendation ||
		effectiveChangeRate === undefined ||
		effectiveChangeRate === null ||
		(showRecommendedPrice && (recommendedPrice === undefined || recommendedPrice === null))
	)
		return (
			<td className="whitespace-nowrap px-2 py-1 align-middle text-sm text-gray-500 border-r border-gray-200" />
		);


	const editing = editor !== undefined && editor.editing && !editor.isPending && !isSubmitted;
	const shownPrice = recommendedPrice;
	const hasPrice = showRecommendedPrice && shownPrice !== undefined && shownPrice !== null;
	const shownPercent = Utilities.roundDouble(effectiveChangeRate * 100, 0);

	const percentText = `${shownPercent > 0 ? '+' : ''}${shownPercent}%${showAsterisk ? '*' : ''}`;
	const percentClassName = Utilities.classNames(
		'text-xs font-medium',
		shownPercent < 0 ? 'text-red-600' : shownPercent > 0 ? 'text-green-600' : 'text-gray-500'
	);

	return (
		<td
			className={Utilities.classNames(
				'whitespace-nowrap px-2 py-1 align-middle text-sm border-r border-gray-200',
				shownPercent < 0 ? 'bg-red-50' : shownPercent > 0 ? 'bg-green-50' : 'bg-gray-50'
			)}
		>
			<div className="grid grid-cols-reco-cell items-center gap-x-0.5">
				{hasPrice && <div className="col-start-1 row-start-1 flex h-5 min-w-0 items-center font-medium text-gray-700">
						<span className="max-w-full shrink-0 truncate" title={Utilities.formatAmount(shownPrice, companyHotel.settings.main.currency, 0)}>
							{Utilities.formatAmount(shownPrice, companyHotel.settings.main.currency, 0)}
						</span>
				</div>}
				<span className="col-start-2 row-start-1 flex h-5 items-center justify-center"><RecommendationInfoButton {...info} /></span>
				<div className="col-start-3 row-start-1 flex h-5 w-5 items-center justify-center">
					{!editing && <RecommendationRightControl state={editor} isSubmitted={isSubmitted} />}
				</div>
				<RecommendationPercentLine rowClassName="row-start-2" state={isSubmitted ? undefined : editor}
					hotelMarketPriceText={showRecommendedPrice && editor?.hotelMarketPrice !== undefined ? Utilities.formatAmount(editor.hotelMarketPrice, companyHotel.settings.main.currency, 0) : undefined}
					previewPriceText={showRecommendedPrice && editor?.previewPrice !== undefined ? Utilities.formatAmount(editor.previewPrice, companyHotel.settings.main.currency, 0) : undefined}
					percentText={percentText} className={percentClassName} />
			</div>
		</td>
	);
};

type RoomTypeRowProps = {
	dailySnapshotState: MarketStateDailySnapshotState;
	companyHotel: CompanyHotel;
	competitorSnapshotsComparison: CompetitorSnapshotsComparison;
	isAnyProvider: boolean;
	showUpdateSuccess: boolean;
	isUpdating: boolean;
	pace?: PmsScope[];
	pickupWindows: PickupWindow[];
	showPickup: boolean;
	canSubmitPrices: boolean;
};

const MarketStateSnapshotDateBasedTableRowDetailsFragment: React.FunctionComponent<RoomTypeRowProps> = ({
	dailySnapshotState,
	companyHotel,
	competitorSnapshotsComparison,
	isAnyProvider,
	showUpdateSuccess,
	isUpdating,
	pace,
	pickupWindows,
	showPickup,
	canSubmitPrices,
}) => {
	const columns = useDateTableColumns();
	const roomMappingTypes = sortRoomMappingTypes(
		dailySnapshotState.marketStateDailySnapshot?.current.findDistinctRoomMappingTypesForLowestItems() ||
			[]
	);
	const { canViewRecommendations } = usePricingRights(companyHotel);
	const { filter } = useMarketStateFilterInputTools();
	const recommendationStates = findValidRecommendationStates(dailySnapshotState, companyHotel, canViewRecommendations);
	const submittedRecommendationStateIds = new Set(
		dailySnapshotState.marketStateDailySnapshot?.submittedRecommendationStateIds ?? []
	);
	const recommendationStatesByRoomMappingType = useMemo(
		() =>
			new Map(
				recommendationStates.map(recommendationState => [
					recommendationState.roomMappingType,
					recommendationState,
				])
			),
		[recommendationStates]
	);

	if (_isEmpty(roomMappingTypes))
		return (
			<tr
				className={Utilities.classNames(
					dailySnapshotState.calendarDay.isPast ? 'opacity-30 bg-gray-200/60' : ''
				)}
			>
				<td colSpan={5 + columns.visible.length}>No Data</td>
			</tr>
		);

	const backgroundColor = dailySnapshotState.calendarDay.isToday ? 'bg-blue-50' : 'bg-cyan-50';

	return roomMappingTypes
		?.filter(item => item != HotelRoomMappingType.NONE)
		.map(item => <RoomTypeRow
			key={`${dailySnapshotState.calendarDay.display}_${item}`}
			item={item}
			recommendationState={recommendationStatesByRoomMappingType.get(item)}
			isSubmitted={recommendationStatesByRoomMappingType.get(item) !== undefined
				&& submittedRecommendationStateIds.has(recommendationStatesByRoomMappingType.get(item)!.id)}
			backgroundColor={backgroundColor}
			dailySnapshotState={dailySnapshotState}
			companyHotel={companyHotel}
			competitorSnapshotsComparison={competitorSnapshotsComparison}
			isAnyProvider={isAnyProvider}
			showUpdateSuccess={showUpdateSuccess}
			isUpdating={isUpdating}
			pace={pace}
			pickupWindows={pickupWindows}
			showPickup={showPickup}
			canSubmitPrices={canSubmitPrices}
		/>);
};

// One room type of an expanded date: its own plane, percent and Pickup; a push acts on this type only.
const RoomTypeRow: React.FunctionComponent<RoomTypeRowProps & {
	item: HotelRoomMappingType;
	recommendationState: PriceRecommendationState | undefined;
	isSubmitted: boolean;
	backgroundColor: string;
}> = ({
	item, recommendationState, isSubmitted, backgroundColor, dailySnapshotState, companyHotel, competitorSnapshotsComparison,
	isAnyProvider, showUpdateSuccess, isUpdating, pace, pickupWindows, showPickup, canSubmitPrices,
}) => {
	const { filter } = useMarketStateFilterInputTools();
	const visibleCompetitors = companyHotel.visibleCompetitors(filter.competitorTagIds);
	const columns = useDateTableColumns();
	const { canViewRecommendations } = usePricingRights(companyHotel);
	const [submitFailure, setSubmitFailure] = useState<string | undefined>(undefined);
	const submit = useRecommendationSubmit({
		companyHotel,
		statDate: dailySnapshotState.calendarDay.display,
		marketUpdatedAt: dailySnapshotState.marketStateDailySnapshot?.current.latestUpdateDate,
		recommendationIds: recommendationState !== undefined ? [recommendationState.id] : [],
		recommendedPrice: findRecommendedPrice(dailySnapshotState, recommendationState, item),
		effectiveChangeRate: recommendationState?.effectiveChangeRate,
		recommendationStates: recommendationState !== undefined ? [recommendationState] : [],
		hotelMarketPrice: findHotelMarketPrice(dailySnapshotState, item),
		enabled: canSubmitPrices && !dailySnapshotState.calendarDay.isPast && recommendationState !== undefined && recommendationState.isValid && !isSubmitted,
		onBlocked: setSubmitFailure,
	});

			return (
				<>
				<tr
					className={Utilities.classNames(
						'rates-room-row h-12',
						dailySnapshotState.calendarDay.isPast ? 'opacity-30 bg-gray-200/60' : ''
					)}
				>
					<td className={`border-r border-gray-200 ${backgroundColor}`} />
					<td
						className={Utilities.classNames(
							'whitespace-nowrap px-2 py-1 align-middle text-sm font-medium text-gray-800 border-r border-gray-200',
							submitFailure !== undefined && submitFailure !== '' ? 'bg-red-50 text-red-700' : backgroundColor)}
						colSpan={3}
					>
						<span className="block truncate" title={companyHotel.findRoomMappingTypeName(item)}>{companyHotel.findRoomMappingTypeName(item)}</span>

					</td>
					<td
						className={`whitespace-nowrap px-1.5 py-1 align-middle text-sm text-gray-500 border-r border-gray-200 ${backgroundColor}`}
					>
						<DisplayMarketStateSnapshotTableHotelPricePureFragment
							marketStateDailySnapshotState={dailySnapshotState}
							roomMappingType={item}
							competitorSnapshots={competitorSnapshotsComparison}
							companyHotel={companyHotel}
							isAnyProvider={isAnyProvider}
							showUpdateSuccess={showUpdateSuccess}
							isUpdating={isUpdating}
						/>
					</td>
					<DateTableOrderedCells
 recommendation={<RenderRecommendationPriceCell
							recommendedPrice={findRecommendedPrice(dailySnapshotState, recommendationState, item)}
							effectiveChangeRate={recommendationState?.effectiveChangeRate}
							companyHotel={companyHotel}
							showRecommendation={recommendationState?.isValid === true}
							isSubmitted={isSubmitted}
							editor={submit}
						info={{ statDate: dailySnapshotState.calendarDay.display, scope: item }}
						/>}
 pickup={<PickupCell pickup={pmsPickup(pace, item)} windows={pickupWindows}
																			label={`${Utilities.formatDate(dailySnapshotState.calendarDay.date, 'ddd DD/MM')} · ${companyHotel.findRoomMappingTypeName(item)}`}
																			className={backgroundColor} />}
 availability={<AvailabilityCell statDate={dailySnapshotState.calendarDay.display} roomMappingType={item} className={backgroundColor} />}
 median={<td
						className={`whitespace-nowrap px-2 py-1 align-middle text-sm text-gray-500 border-r border-gray-200 text-center ${backgroundColor}`}
					>
						<MarketStateSnapshotTableMedianFragment
							calendarDay={dailySnapshotState.calendarDay}
							roomMappingType={item}
						/>
					</td>}
 competitors={visibleCompetitors.map(competitor => (
						<td
							className={`whitespace-nowrap px-1.5 py-1 align-middle text-sm text-gray-500 border-r ${backgroundColor}`}
							key={competitor.id}
						>
							<DisplayMarketStateSnapshotTableHotelPricePureFragment
								marketStateDailySnapshotState={dailySnapshotState}
								competitor={competitor}
								roomMappingType={item}
								competitorSnapshots={competitorSnapshotsComparison}
								companyHotel={companyHotel}
								isAnyProvider={isAnyProvider}
								showUpdateSuccess={showUpdateSuccess}
								isUpdating={isUpdating}
							/>
						</td>
					))}
 />
				</tr>
				{submitFailure !== undefined && submitFailure !== '' && <tr><td colSpan={5 + columns.visible.length}
					className="bg-red-50 px-3 py-2 text-xs text-red-700"><span role="alert" className="sticky left-0 block w-fit max-w-xs whitespace-normal break-words">{submitFailure}</span></td></tr>}
				</>
			);
};

const isMarketStateTableProcess = (
	processRequestExecutionState: { process: Process } | null | undefined
) => {
	return (
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE ||
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE_OTA ||
		processRequestExecutionState?.process === Process.PRICE_RECOMMENDATION
	);
};

export default React.memo(MarketStateSnapshotDateBasedTableRowFragment, (prevProps, nextProps) => {
	return (
		prevProps.dailySnapshotState === nextProps.dailySnapshotState &&
		prevProps.onSelectedCalendarDay === nextProps.onSelectedCalendarDay &&
		prevProps.detailsRequestId === nextProps.detailsRequestId &&
		prevProps.enforceOpenDetails === nextProps.enforceOpenDetails &&
		prevProps.dateSelectable === nextProps.dateSelectable &&
		prevProps.dateSelected === nextProps.dateSelected &&
		prevProps.bulkFailureReason === nextProps.bulkFailureReason &&
		prevProps.onRecommendationSelectedChange === nextProps.onRecommendationSelectedChange &&
		prevProps.pace === nextProps.pace &&
		prevProps.pickupWindows === nextProps.pickupWindows &&
		prevProps.showPickup === nextProps.showPickup &&
		prevProps.canSubmitPrices === nextProps.canSubmitPrices
	);
});
